<script>
  // FilesModal — full-screen overlay browsing every generated image in
  // output/. Left pane = job list (newest first), right pane = grid of
  // user-facing images for the selected job. Header has search filter.
  //
  // Backend:
  //   GET /api/admin/files                    → list of job dirs
  //   GET /api/admin/files/{job_id}           → list of files in 1 job
  //
  // Image files are returned with `is_image=true` + a `/output/...` URL
  // the static mount serves. Each image card has both "open in tab"
  // (click image) and an explicit Download button.
  import { onMount } from "svelte";
  import { filesApi } from "../api/client.js";

  let { onClose } = $props();

  // --- Master pane state ---
  let jobs = $state([]);
  let loadingJobs = $state(false);
  let jobsError = $state(null);
  let search = $state("");

  // --- Detail pane state ---
  let selectedJobId = $state(null);
  let files = $state([]);
  let loadingFiles = $state(false);
  let filesError = $state(null);
  let selectedJobInputUrl = $state(null);

  onMount(() => { loadJobs(); });

  async function loadJobs() {
    loadingJobs = true;
    jobsError = null;
    try {
      const data = await filesApi.listJobs();
      jobs = data.jobs || [];
      // Auto-select newest job so the right pane isn't empty on open.
      if (jobs.length > 0 && !selectedJobId) {
        await selectJob(jobs[0].job_id, jobs[0].input_url);
      }
    } catch (err) {
      jobsError = err.message;
    } finally {
      loadingJobs = false;
    }
  }

  async function selectJob(jobId, inputUrl) {
    if (selectedJobId === jobId && files.length > 0) return; // already loaded
    selectedJobId = jobId;
    selectedJobInputUrl = inputUrl || null;
    loadingFiles = true;
    filesError = null;
    files = [];
    try {
      const data = await filesApi.listJobFiles(jobId);
      files = data.files || [];
    } catch (err) {
      filesError = err.message;
    } finally {
      loadingFiles = false;
    }
  }

  const filteredJobs = $derived(
    !search ? jobs : jobs.filter((j) =>
      j.job_id.toLowerCase().includes(search.toLowerCase())
    )
  );

  // --- Formatting helpers ---
  const VN_FMT = new Intl.DateTimeFormat("vi-VN", {
    timeZone: "Asia/Ho_Chi_Minh",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
  function fmtTime(epochSec) {
    if (!epochSec) return "—";
    const d = new Date(epochSec * 1000);
    if (Number.isNaN(d.getTime())) return "—";
    return VN_FMT.format(d).replace(",", "");
  }
  function fmtBytes(n) {
    if (n == null) return "—";
    if (n < 1024) return `${n}B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)}KB`;
    if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)}MB`;
    return `${(n / 1024 / 1024 / 1024).toFixed(2)}GB`;
  }

  function handleKey(e) { if (e.key === "Escape") onClose?.(); }
</script>

<svelte:window onkeydown={handleKey} />

<div class="modal-backdrop" onclick={onClose} role="presentation">
  <div class="modal" role="dialog" aria-modal="true" aria-labelledby="files-modal-title" tabindex="-1"
       onclick={(e) => e.stopPropagation()}
       onkeydown={(e) => e.stopPropagation()}>
    <header class="modal-head">
      <h2 id="files-modal-title">🖼️ Files <span class="muted">(toàn bộ ảnh đã sinh)</span></h2>
      <div class="modal-actions">
        <input type="search" placeholder="Tìm job id..." bind:value={search} />
        <button class="link-btn" onclick={loadJobs} disabled={loadingJobs}>
          {loadingJobs ? "Đang tải..." : "↻ Refresh"}
        </button>
        <button class="close-btn" onclick={onClose} title="Đóng (Esc)">✕</button>
      </div>
    </header>

    <div class="modal-body">
      <!-- Master: job list -->
      <aside class="master">
        {#if jobsError}
          <div class="error inline">{jobsError}</div>
        {/if}
        {#if loadingJobs && jobs.length === 0}
          <p class="muted center small">Đang tải...</p>
        {:else if filteredJobs.length === 0}
          <p class="muted center small">
            {search ? "Không có job nào khớp." : "Chưa có file nào trong output/."}
          </p>
        {:else}
          <div class="job-list">
            {#each filteredJobs as j (j.job_id)}
              <button class="job-row" class:active={selectedJobId === j.job_id}
                      onclick={() => selectJob(j.job_id, j.input_url)}>
                {#if j.input_url}
                  <img class="job-thumb" src={j.input_url} alt="" loading="lazy" />
                {:else}
                  <div class="job-thumb job-thumb-empty">?</div>
                {/if}
                <div class="job-meta">
                  <code class="job-id">{j.job_id.slice(0, 8)}</code>
                  <div class="job-bottom">
                    <span>{j.file_count} files</span>
                    <span>{fmtBytes(j.size_bytes)}</span>
                  </div>
                  <span class="job-time">{fmtTime(j.mtime)}</span>
                </div>
              </button>
            {/each}
          </div>
        {/if}
      </aside>

      <!-- Detail: files for selected job -->
      <main class="detail">
        {#if !selectedJobId}
          <p class="muted center">Chọn 1 job ở bên trái.</p>
        {:else}
          <div class="detail-head">
            <div>
              <h3>{selectedJobId}</h3>
              <span class="muted small">{files.length} ảnh</span>
            </div>
            <a class="link-btn" href={`#/job/${selectedJobId}`} onclick={onClose}>
              📥 Mở job này
            </a>
          </div>

          {#if filesError}
            <div class="error">Lỗi: {filesError}</div>
          {:else if loadingFiles}
            <p class="muted center">Đang tải...</p>
          {:else if files.length === 0}
            <p class="muted center">Không có ảnh nào trong job này.</p>
          {:else}
            <div class="files-grid">
              {#each files as f (f.url)}
                <div class="file-card">
                  <a class="file-img-link" href={f.url} target="_blank" rel="noopener" title={f.name}>
                    <img src={f.url} alt={f.name} loading="lazy" />
                  </a>
                  <div class="file-meta">
                    <div class="file-meta-left">
                      <span class="file-name" title={f.name}>{f.name}</span>
                      <span class="file-sub">{fmtBytes(f.size_bytes)} · {fmtTime(f.mtime)}</span>
                    </div>
                    <a class="download-btn" href={f.url} download={f.name} title="Download">⬇</a>
                  </div>
                </div>
              {/each}
            </div>
          {/if}
        {/if}
      </main>
    </div>
  </div>
</div>

<style>
  .modal-backdrop {
    position: fixed; inset: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex; align-items: center; justify-content: center;
    z-index: 200;
    padding: 20px;
  }

  .modal {
    background: var(--bg-elevated);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    width: min(1300px, 100%);
    height: 90vh;
    display: flex;
    flex-direction: column;
    box-shadow: var(--shadow-md);
  }

  .modal-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 18px;
    border-bottom: 1px solid var(--border);
    flex-wrap: wrap;
  }
  .modal-head h2 { margin: 0; font-size: 1rem; color: var(--text-strong); }
  .modal-head .muted { font-weight: 400; font-size: 0.8rem; }

  .modal-actions {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
  }
  .modal-actions input { min-width: 200px; }
  .close-btn {
    width: 30px; height: 30px;
    border-radius: 6px;
    background: var(--panel-2);
    color: var(--muted);
    font-size: 0.9rem;
    border: 1px solid var(--border);
    transition: background 0.15s, color 0.15s;
  }
  .close-btn:hover { background: var(--panel); color: var(--text); }

  .modal-body {
    display: grid;
    grid-template-columns: 320px 1fr;
    flex: 1;
    min-height: 0;
  }

  /* Master pane — job list */
  .master {
    border-right: 1px solid var(--border);
    overflow-y: auto;
    padding: 10px;
    min-height: 0;
  }

  .job-list { display: flex; flex-direction: column; gap: 4px; }
  .job-row {
    display: grid;
    grid-template-columns: 48px 1fr;
    gap: 10px;
    align-items: center;
    padding: 6px 8px;
    background: transparent;
    border: 1px solid transparent;
    border-radius: var(--radius-sm);
    text-align: left;
    transition: border-color 0.15s, background 0.15s;
    cursor: pointer;
  }
  .job-row:hover {
    border-color: var(--border);
    background: var(--panel-2);
  }
  .job-row.active {
    border-color: var(--accent);
    background: rgba(124, 158, 255, 0.06);
  }

  .job-thumb {
    width: 48px; height: 48px;
    border-radius: 4px;
    object-fit: cover;
    background: var(--bg);
    border: 1px solid var(--border);
  }
  .job-thumb-empty {
    display: flex; align-items: center; justify-content: center;
    color: var(--muted); font-size: 1rem; font-weight: 600;
  }

  .job-meta { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
  .job-id {
    font-family: ui-monospace, monospace;
    font-size: 0.78rem;
    font-weight: 600;
    color: var(--text-strong);
  }
  .job-bottom {
    display: flex; gap: 8px;
    font-size: 0.7rem;
    color: var(--muted);
    font-variant-numeric: tabular-nums;
  }
  .job-time {
    font-size: 0.68rem;
    color: var(--muted);
    font-variant-numeric: tabular-nums;
  }

  /* Detail pane — file grid */
  .detail {
    padding: 14px 18px;
    overflow-y: auto;
    min-height: 0;
  }

  .detail-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;
    padding-bottom: 10px;
    border-bottom: 1px solid var(--border);
  }
  .detail-head h3 {
    margin: 0;
    font-size: 0.95rem;
    font-family: ui-monospace, monospace;
    color: var(--text-strong);
  }
  .detail-head .small { font-size: 0.74rem; }

  .files-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 10px;
  }
  .file-card {
    display: flex; flex-direction: column;
    background: var(--panel); border: 1px solid var(--border);
    border-radius: var(--radius-sm); overflow: hidden;
    transition: border-color 0.15s, transform 0.1s;
  }
  .file-card:hover { border-color: var(--accent); transform: translateY(-1px); }
  .file-img-link { display: block; }
  .file-card img {
    width: 100%; aspect-ratio: 1 / 1; object-fit: contain;
    background: #0d0f16; display: block;
  }
  .file-meta {
    display: flex; align-items: center; justify-content: space-between;
    gap: 6px;
    padding: 6px 8px;
    border-top: 1px solid var(--border);
  }
  .file-meta-left {
    display: flex; flex-direction: column; min-width: 0;
    gap: 1px;
  }
  .file-name {
    font-size: 0.72rem;
    font-family: ui-monospace, monospace;
    color: var(--text);
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .file-sub {
    font-size: 0.66rem;
    color: var(--muted);
    font-variant-numeric: tabular-nums;
  }
  .download-btn {
    flex-shrink: 0;
    display: inline-flex; align-items: center; justify-content: center;
    width: 22px; height: 22px;
    background: var(--panel-2); border: 1px solid var(--border);
    border-radius: 4px;
    color: var(--text); text-decoration: none;
    font-size: 0.78rem;
    transition: background 0.15s, border-color 0.15s, color 0.15s;
  }
  .download-btn:hover {
    background: var(--accent); border-color: var(--accent);
    color: var(--bg);
  }

  .error {
    padding: 10px 14px;
    margin-bottom: 12px;
    background: rgba(248, 113, 113, 0.08);
    border: 1px solid rgba(248, 113, 113, 0.3);
    border-radius: var(--radius-sm);
    color: #ff8585;
    font-size: 0.82rem;
  }
  .error.inline { padding: 6px 10px; font-size: 0.76rem; margin: 6px; }
  .muted { color: var(--muted); font-size: 0.85rem; }
  .center { text-align: center; padding: 30px 0; }
  .small { font-size: 0.74rem; }

  /* Stack on narrow screens */
  @media (max-width: 720px) {
    .modal-body {
      grid-template-columns: 1fr;
    }
    .master {
      max-height: 30vh;
      border-right: none;
      border-bottom: 1px solid var(--border);
    }
  }
</style>
